/**
 * File Type Detection and Classification Utilities
 * Centralizes all file type detection logic for preview and rendering
 */

// File categories for preview routing
export const FILE_CATEGORIES = {
  IMAGE: "image",
  PDF: "pdf",
  VIDEO: "video",
  AUDIO: "audio",
  DOCUMENT_DOCX: "docx",
  DOCUMENT_XLSX: "xlsx",
  TEXT: "text",
  DICOM: "dicom",
  MODEL_3D: "3d",
  ARCHIVE: "archive",
  UNKNOWN: "unknown"
}

// Supported file extensions by category
const FILE_EXTENSIONS = {
  [FILE_CATEGORIES.IMAGE]: [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "webp",
    "svg",
    "bmp",
    "tiff",
    "tif",
    "heic",
    "heif"
  ],
  [FILE_CATEGORIES.PDF]: ["pdf"],
  [FILE_CATEGORIES.VIDEO]: [
    "mp4",
    "webm",
    "ogv",
    "mov",
    "avi",
    "m4v",
    "3gp",
    "mkv",
    "wmv",
    "flv"
  ],
  [FILE_CATEGORIES.AUDIO]: [
    "mp3",
    "wav",
    "ogg",
    "mpeg",
    "aac",
    "flac",
    "wma",
    "m4a"
  ],
  [FILE_CATEGORIES.DOCUMENT_DOCX]: ["docx"],
  [FILE_CATEGORIES.DOCUMENT_XLSX]: ["xlsx", "xls", "csv"],
  [FILE_CATEGORIES.TEXT]: [
    "txt",
    "log",
    "json",
    "xml",
    "html",
    "htm",
    "rtf",
    "md",
    "yaml",
    "yml",
    "ini",
    "conf",
    "config"
  ],
  [FILE_CATEGORIES.DICOM]: [
    "dcm",
    "dicom",
    "dic",
    "ima",
    "nii",
    "nrrd",
    "mhd",
    "mha",
    "vtk",
    "vti",
    "vtp"
  ],
  [FILE_CATEGORIES.MODEL_3D]: [
    "stl",
    "obj",
    "ply",
    "gltf",
    "glb",
    "3ds",
    "dae",
    "fbx",
    "dxf",
    "dwg"
  ],
  [FILE_CATEGORIES.ARCHIVE]: ["zip", "rar", "7z", "tar", "gz", "tgz", "bz2"]
}

// MIME type prefixes for quick detection
const MIME_PREFIXES = {
  [FILE_CATEGORIES.IMAGE]: ["image/"],
  [FILE_CATEGORIES.VIDEO]: ["video/"],
  [FILE_CATEGORIES.AUDIO]: ["audio/"],
  [FILE_CATEGORIES.PDF]: ["application/pdf"],
  [FILE_CATEGORIES.DOCUMENT_DOCX]: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ],
  [FILE_CATEGORIES.DOCUMENT_XLSX]: [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv"
  ],
  [FILE_CATEGORIES.TEXT]: [
    "text/plain",
    "text/xml",
    "application/json",
    "application/xml"
  ],
  [FILE_CATEGORIES.DICOM]: ["application/dicom"],
  [FILE_CATEGORIES.ARCHIVE]: [
    "application/zip",
    "application/x-rar-compressed",
    "application/x-7z-compressed"
  ]
}

// Categories that support preview
const PREVIEWABLE_CATEGORIES = [
  FILE_CATEGORIES.IMAGE,
  FILE_CATEGORIES.PDF,
  FILE_CATEGORIES.VIDEO,
  FILE_CATEGORIES.AUDIO,
  FILE_CATEGORIES.DOCUMENT_DOCX,
  FILE_CATEGORIES.DOCUMENT_XLSX,
  FILE_CATEGORIES.TEXT,
  FILE_CATEGORIES.DICOM
]

/**
 * Extract file extension from filename
 * @param {string} filename - The filename
 * @returns {string} - Lowercase extension without dot
 */
export const getFileExtension = (filename) => {
  if (!filename) return ""
  const parts = filename.toLowerCase().split(".")
  return parts.length > 1 ? parts.pop() : ""
}

/**
 * Detect file category from filename and mimetype
 * @param {string} filename - The filename
 * @param {string} mimetype - The MIME type
 * @returns {string} - One of FILE_CATEGORIES values
 */
export const detectFileCategory = (filename, mimetype = "") => {
  const extension = getFileExtension(filename)
  const mime = (mimetype || "").toLowerCase()

  // Check MIME type first (more reliable)
  if (mime) {
    for (const [category, prefixes] of Object.entries(MIME_PREFIXES)) {
      if (
        prefixes.some((prefix) => mime.startsWith(prefix) || mime === prefix)
      ) {
        return category
      }
    }
  }

  // Fallback to extension
  if (extension) {
    for (const [category, extensions] of Object.entries(FILE_EXTENSIONS)) {
      if (extensions.includes(extension)) {
        return category
      }
    }
  }

  return FILE_CATEGORIES.UNKNOWN
}

/**
 * Check if a file supports preview
 * @param {Object} attachment - Attachment object with filename/fileName and mimetype/fileType
 * @returns {boolean}
 */
export const isSupportedPreview = (attachment) => {
  if (!attachment) return false

  const filename =
    attachment.filename ||
    attachment.fileName ||
    attachment.name ||
    attachment.originalName ||
    ""
  const mimetype =
    attachment.mimetype ||
    attachment.fileType ||
    attachment.mime_type ||
    attachment.type ||
    attachment.mimeType ||
    ""

  const category = detectFileCategory(filename, mimetype)
  return PREVIEWABLE_CATEGORIES.includes(category)
}

/**
 * Get all supported extensions for a category
 * @param {string} category - One of FILE_CATEGORIES
 * @returns {Array<string>}
 */
export const getExtensionsForCategory = (category) => {
  return FILE_EXTENSIONS[category] || []
}

/**
 * Check if extension is supported for a specific category
 * @param {string} extension - File extension
 * @param {string} category - One of FILE_CATEGORIES
 * @returns {boolean}
 */
export const isExtensionInCategory = (extension, category) => {
  const extensions = FILE_EXTENSIONS[category] || []
  return extensions.includes(extension.toLowerCase())
}

/**
 * Get human-readable category name
 * @param {string} category - One of FILE_CATEGORIES
 * @returns {string}
 */
export const getCategoryDisplayName = (category) => {
  const displayNames = {
    [FILE_CATEGORIES.IMAGE]: "Imagem",
    [FILE_CATEGORIES.PDF]: "PDF",
    [FILE_CATEGORIES.VIDEO]: "Vídeo",
    [FILE_CATEGORIES.AUDIO]: "Áudio",
    [FILE_CATEGORIES.DOCUMENT_DOCX]: "Documento Word",
    [FILE_CATEGORIES.DOCUMENT_XLSX]: "Planilha",
    [FILE_CATEGORIES.TEXT]: "Texto",
    [FILE_CATEGORIES.DICOM]: "Imagem Médica (DICOM)",
    [FILE_CATEGORIES.MODEL_3D]: "Modelo 3D",
    [FILE_CATEGORIES.ARCHIVE]: "Arquivo Compactado",
    [FILE_CATEGORIES.UNKNOWN]: "Arquivo"
  }
  return displayNames[category] || displayNames[FILE_CATEGORIES.UNKNOWN]
}

/**
 * Sanitize S3 presigned URL by removing problematic query parameters
 * that may cause encoding issues with non-ASCII characters
 * @param {string} url - Original URL
 * @returns {string} Sanitized URL
 */
export const sanitizePresignedUrl = (url) => {
  if (!url) return url

  try {
    const urlObj = new URL(url)

    // Remove response-content-disposition if it contains non-ASCII characters
    // This prevents "Header value cannot be represented using ISO-8859-1" errors from S3
    const contentDisposition = urlObj.searchParams.get(
      "response-content-disposition"
    )
    if (contentDisposition && /[^\x00-\x7F]/.test(contentDisposition)) {
      urlObj.searchParams.delete("response-content-disposition")

      console.warn(
        "[FileTypeUtils] Removed response-content-disposition with non-ASCII characters from URL"
      )
    }

    return urlObj.toString()
  } catch (error) {
    // If URL parsing fails, return original
    console.error("[FileTypeUtils] Error sanitizing URL:", error)
    return url
  }
}

/**
 * Extract attachment properties in a normalized way
 * @param {Object} attachment - Attachment object
 * @returns {Object} Normalized properties
 */
export const normalizeAttachment = (attachment) => {
  if (!attachment) return null

  const rawUrl =
    attachment.download_url ||
    attachment.signed_download_url ||
    attachment.url ||
    attachment.fileUrl ||
    ""

  return {
    filename:
      attachment.filename ||
      attachment.fileName ||
      attachment.name ||
      attachment.originalName ||
      "",
    mimetype:
      attachment.mimetype ||
      attachment.fileType ||
      attachment.mime_type ||
      attachment.type ||
      attachment.mimeType ||
      "",
    url: sanitizePresignedUrl(rawUrl), // Sanitize URL to prevent encoding issues
    size:
      attachment.file_size || attachment.filesize || attachment.fileSize || 0,
    sizehuman: attachment.file_size_human || null,
    description: attachment.attachment_name || attachment.description || "",
    createdAt: attachment.created_at || attachment.createdAt || null
  }
}
